import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from "../authHeaders";
import { useTableControls } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';
import type { Column } from '../hooks/useTableControls';

export function LicensesPage() {
  const { data: licensesData, isLoading: licensesLoading, error: licensesError } = useQuery({
    queryKey: ['licenses'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/licenses');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: wasteData, isLoading: wasteLoading } = useQuery({
    queryKey: ['licenses-waste'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/licenses/waste');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: reclaimData, isLoading: reclaimLoading } = useQuery({
    queryKey: ['licenses-reclaim-opportunity'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/licenses/reclaim-opportunity');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: renewalData, isLoading: renewalLoading } = useQuery({
    queryKey: ['licenses-renewal-calendar'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/licenses/renewal-calendar');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const isLoading = licensesLoading || wasteLoading || reclaimLoading || renewalLoading;
  const hasError = licensesError;

  const entitlements = licensesData?.entitlements || [];
  const wasteCategories = wasteData?.categories || {};
  const reclaimTotal = reclaimData?.total ?? null;
  const reclaimMethodology = reclaimData?.methodology || '';
  const renewals = renewalData?.items || [];

  const wasteCards = [
    { key: 'assigned_never_used', label: 'Assigned, Never Used', data: wasteCategories.assigned_never_used },
    { key: 'assigned_to_separated', label: 'Assigned to Separated Employees', data: wasteCategories.assigned_to_separated },
    { key: 'over_tiered', label: 'Over-Tiered', data: wasteCategories.over_tiered },
    { key: 'unassigned_purchased', label: 'Unassigned but Purchased', data: wasteCategories.unassigned_purchased },
  ];

  const now = new Date();
  const ninetyDaysOut = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

  // Entitlement Inventory columns
  const entitlementColumns: Column<any>[] = [
    { key: 'product', label: 'Product' },
    { key: 'publisher', label: 'Publisher', render: (row: any) => row.publisher || '—' },
    { key: 'seats_purchased', label: 'Seats Purchased', render: (row: any) => <span className="td--num">{row.seats_purchased ?? '—'}</span> },
    { key: 'seats_assigned', label: 'Seats Assigned', render: (row: any) => <span className="td--num">{row.seats_assigned ?? '—'}</span> },
    { key: 'seats_used', label: 'Seats Used', render: (row: any) => <span className="td--num">{row.seats_used ?? '—'}</span> },
    { key: 'unit_cost', label: 'Unit Cost', render: (row: any) => <span className="td--num">{row.unit_cost != null ? `$${row.unit_cost}` : '—'}</span> },
    { key: 'annual_spend', label: 'Annual Spend', render: (row: any) => <span className="td--num">{row.annual_spend != null ? `$${row.annual_spend.toLocaleString()}` : '—'}</span> },
    { key: 'renewal_date', label: 'Renewal Date', dateField: true, render: (row: any) => <span className="td--num">{row.renewal_date ? new Date(row.renewal_date).toLocaleDateString() : '—'}</span> },
  ];

  // Renewal Calendar columns
  const renewalColumns: Column<any>[] = [
    { key: 'product', label: 'Product' },
    { key: 'renewal_date', label: 'Renewal Date', dateField: true, render: (row: any) => <span className="td--num">{row.renewal_date ? new Date(row.renewal_date).toLocaleDateString() : '—'}</span> },
    {
      key: 'days_until',
      label: 'Days Until Renewal',
      accessor: (row: any) => {
        const d = row.renewal_date ? new Date(row.renewal_date) : null;
        return d ? Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
      },
      render: (row: any) => {
        const renewalDate = row.renewal_date ? new Date(row.renewal_date) : null;
        const daysUntil = renewalDate ? Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
        return <span className="td--num">{daysUntil != null ? `${daysUntil}d` : '—'}</span>;
      },
    },
    {
      key: 'action_required',
      label: 'Action Required',
      accessor: (row: any) => {
        const renewalDate = row.renewal_date ? new Date(row.renewal_date) : null;
        const inWindow = renewalDate && renewalDate >= now && renewalDate <= ninetyDaysOut;
        return inWindow ? 'Action needed' : '';
      },
      render: (row: any) => {
        const renewalDate = row.renewal_date ? new Date(row.renewal_date) : null;
        const inWindow = renewalDate && renewalDate >= now && renewalDate <= ninetyDaysOut;
        return inWindow
          ? <span className="pill pill--warn"><i></i> Action needed</span>
          : '—';
      },
    },
  ];

  const entitlementsTable = useTableControls(entitlements, entitlementColumns, { storageKey: 'licenses-entitlements' });
  const renewalsTable = useTableControls(renewals, renewalColumns, { storageKey: 'licenses-renewals' });

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Licenses</p>
              <h1 className="pagehead__title">License &amp; Entitlement Savings</h1>
              <div className="pagehead__facts">
                <span className="fact">Reclaim opportunity across software licenses</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading license data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Licenses</p>
              <h1 className="pagehead__title">License &amp; Entitlement Savings</h1>
              <div className="pagehead__facts">
                <span className="fact">Reclaim opportunity across software licenses</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load license data</h3>
                <p className="state__body">
                  Could not connect to the licenses API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => window.location.reload()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Licenses</p>
            <h1 className="pagehead__title">License &amp; Entitlement Savings</h1>
            <div className="pagehead__facts">
              <span className="fact">Reclaim opportunity across software licenses and entitlements</span>
              {reclaimMethodology && <span className="fact">Methodology: <b>{reclaimMethodology}</b></span>}
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Data coverage notice */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">Data Coverage</p>
            <p className="notice__body">
              Usage telemetry is not available for all products. Where seat usage data is missing,
              savings estimates are based on assignment records alone and may overstate actual utilization.
              Products without usage telemetry are flagged in the entitlement table below.
            </p>
          </div>
        </div>

        {/* Headline Metric — Total Reclaim Opportunity */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Reclaim summary">
          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Reclaim</p>
              <p className="ind__full">Total Reclaim Opportunity</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{reclaimTotal != null ? `$${reclaimTotal.toLocaleString()}` : '—'}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Opportunity</b> — total potential savings across all savings categories
            </p>
          </div>
        </div>

        {/* Savings Categories */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Savings Analysis</p>
              <h2 className="box__title">Savings Categories</h2>
            </div>
          </div>
          <div className="box__body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Savings categories">
              {wasteCards.map(({ key, label, data }) => (
                <div className="ind" key={key}>
                  <div className="ind__head">
                    <p className="ind__abbr">{label}</p>
                  </div>
                  <div className="ind__value">
                    <p className="ind__v">{data?.amount != null ? `$${data.amount.toLocaleString()}` : '—'}</p>
                  </div>
                  {data?.count != null && (
                    <p className="ind__reading">
                      <i></i> <b>{data.count}</b> entitlement{data.count !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Entitlement Table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Inventory</p>
              <h2 className="box__title">Entitlement Inventory</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {entitlements.length > 0 ? (
              <DataTable
                columns={entitlementColumns}
                visibleColumns={entitlementsTable.visibleColumns}
                data={entitlementsTable.paged}
                search={entitlementsTable.search}
                onSearchChange={entitlementsTable.setSearch}
                sortKey={entitlementsTable.sortKey}
                sortDir={entitlementsTable.sortDir}
                onSort={entitlementsTable.setSort}
                page={entitlementsTable.page}
                totalPages={entitlementsTable.totalPages}
                totalFiltered={entitlementsTable.totalFiltered}
                pageSize={entitlementsTable.pageSize}
                onPageChange={entitlementsTable.setPage}
                onPageSizeChange={entitlementsTable.setPageSize}
                activeFilters={entitlementsTable.activeFilters}
                onFilter={entitlementsTable.setFilter}
                onClearFilters={entitlementsTable.clearFilters}
                searchPlaceholder="Search entitlements..."
                keyAccessor={(row: any) => row.product}
                emptyMessage="No entitlements reported."
                timeRange={entitlementsTable.timeRange}
                onTimeRangeChange={entitlementsTable.setTimeRange}
                hiddenColumns={entitlementsTable.hiddenColumns}
                onToggleColumn={entitlementsTable.toggleColumn}
                selectedIds={entitlementsTable.selectedIds}
                onToggleSelect={entitlementsTable.toggleSelect}
                onSelectAll={entitlementsTable.selectAll}
                onClearSelection={entitlementsTable.clearSelection}
                savedViews={entitlementsTable.savedViews}
                onSaveView={entitlementsTable.saveView}
                onLoadView={entitlementsTable.loadView}
                onDeleteView={entitlementsTable.deleteView}
                onExportCSV={entitlementsTable.exportCSV}
                onExportExcel={entitlementsTable.exportExcel}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <h3 className="state__title">No entitlements reported</h3>
                <p className="state__body">
                  License entitlement data has not been ingested yet. Configure your license sources to populate this view.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Renewal Calendar */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Calendar</p>
              <h2 className="box__title">Renewal Calendar — 90-Day Action Window</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {renewals.length > 0 ? (
              <DataTable
                columns={renewalColumns}
                visibleColumns={renewalsTable.visibleColumns}
                data={renewalsTable.paged}
                search={renewalsTable.search}
                onSearchChange={renewalsTable.setSearch}
                sortKey={renewalsTable.sortKey}
                sortDir={renewalsTable.sortDir}
                onSort={renewalsTable.setSort}
                page={renewalsTable.page}
                totalPages={renewalsTable.totalPages}
                totalFiltered={renewalsTable.totalFiltered}
                pageSize={renewalsTable.pageSize}
                onPageChange={renewalsTable.setPage}
                onPageSizeChange={renewalsTable.setPageSize}
                activeFilters={renewalsTable.activeFilters}
                onFilter={renewalsTable.setFilter}
                onClearFilters={renewalsTable.clearFilters}
                searchPlaceholder="Search renewals..."
                keyAccessor={(row: any) => row.product}
                emptyMessage="No renewals in the next 90 days."
                timeRange={renewalsTable.timeRange}
                onTimeRangeChange={renewalsTable.setTimeRange}
                hiddenColumns={renewalsTable.hiddenColumns}
                onToggleColumn={renewalsTable.toggleColumn}
                selectedIds={renewalsTable.selectedIds}
                onToggleSelect={renewalsTable.toggleSelect}
                onSelectAll={renewalsTable.selectAll}
                onClearSelection={renewalsTable.clearSelection}
                savedViews={renewalsTable.savedViews}
                onSaveView={renewalsTable.saveView}
                onLoadView={renewalsTable.loadView}
                onDeleteView={renewalsTable.deleteView}
                onExportCSV={renewalsTable.exportCSV}
                onExportExcel={renewalsTable.exportExcel}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <h3 className="state__title">No renewals in the next 90 days</h3>
                <p className="state__body">
                  No license renewals are scheduled within the 90-day action window.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
